import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { Alert, INotification, INotificationListResponse } from '@/interfaces/common.interface';
import { timeAgo } from '@/utility/helper';
import { AlertTriangle, AlertCircle, Info, BellOff, CheckCheck } from 'lucide-react';
import { useAppDispatch, useAppSelector, RootState } from '@/redux/store';
import { setNotificationCount } from '@/redux/features/UserSlice';
import Pagination from '@/components/common/Pagination';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { Plus, Send } from 'lucide-react';
import { RESOURCES } from '@/context/permissions';
import { usePermissions } from '@/hooks/usePermissions';


export default function NotificationsPage() {
    const dispatch = useAppDispatch();
    const { notificationCount } = useAppSelector((state: RootState) => state.user);
    const [notifications, setNotifications] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useRoleNavigate();
    const { canCreate } = usePermissions();

    const [markingAllRead, setMarkingAllRead] = useState(false);

    const severityIcons = { critical: AlertTriangle, warning: AlertCircle, info: Info };
    const severityColors = { critical: 'text-destructive', warning: 'text-warning', info: 'text-info' };

    const fetchAllNotifications = async (pagenum: number) => {
        try {
            setLoading(true);
            const response = await callApi<INotificationListResponse>(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'GET',
                data: { page: pagenum, limit: 10 }
            });

            const rawNotifications = response?.data?.notifications || [];
            if (response?.data?.pageInfo) {
                setTotalPages(response?.data?.pageInfo?.totalPages ?? 1);
            }

            const mappedAlerts: Alert[] = rawNotifications.map((n: INotification) => {
                let title = 'Notification';
                if (n.message?.toLowerCase().includes('driver')) title = 'Driver Update';
                else if (n.message?.toLowerCase().includes('order')) title = 'Order Update';

                return {
                    id: n.id,
                    severity: 'info' as const,
                    title: title,
                    message: n.message,
                    read: n.seen,
                    createdAt: n.createdAt,
                    actionUrl: n.payload?.orderId ? RoutesLink.ViewOrders.replace(':id', n.payload?.orderId) : n.payload?.returnRequestId ? RoutesLink.ViewReturn.replace(":id", n.payload?.returnRequestId) : undefined
                };
            });
            console.log("mappedAlerts", mappedAlerts);


            setNotifications(mappedAlerts);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id: string, isAlreadyRead: boolean) => {
        if (isAlreadyRead) return;
        try {
            await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'PATCH',
                data: {
                    ids: [id],
                    all: false,
                    action: 'seen'
                }
            });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            if (notificationCount > 0) {
                dispatch(setNotificationCount(notificationCount - 1));
            }
        } catch (error) {
            console.error('Failed to mark notification as read', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            setMarkingAllRead(true);
            await callApi(`${API_URL}${API_ENDPOINT.notification}/user`, {
                method: 'PATCH',
                data: {
                    ids: [],
                    all: true,
                    action: 'seen'
                }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            dispatch(setNotificationCount(0));
        } catch (error) {
            ThrowError(error);
        } finally {
            setMarkingAllRead(false);
        }
    };

    useEffect(() => {
        fetchAllNotifications(page);
    }, [page]);


    return (
        <Layout>
            <div className="">
                <MainHeading title="Notifications center">
                    <div className="flex items-center gap-3">
                        {canCreate(RESOURCES.NOTIFICATION) && (
                            <button
                                onClick={() => navigate(RoutesLink.CreateNotification)}
                                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm cursor-pointer"
                            >
                                <Plus className="w-4 h-4" />
                                Send Notification
                            </button>
                        )}
                        {notifications.some(n => !n.read) && (
                            <button
                                onClick={handleMarkAllRead}
                                disabled={markingAllRead}
                                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-all border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {markingAllRead ? (
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <CheckCheck className="w-4 h-4" />
                                )}
                                {markingAllRead ? 'Marking...' : 'Mark all as read'}
                            </button>
                        )}
                    </div>
                </MainHeading>
                <SectionCard title="All Notifications" className="min-h-[600px] flex flex-col">
                    <div className="flex-1">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-muted-foreground animate-pulse">Fetching your alerts...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-[400px] text-center space-y-4">
                                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                                    <BellOff className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold">No notifications found</h3>
                                    <p className="text-sm text-muted-foreground">We'll let you know when something important happens.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {notifications.map((alert) => {
                                    const Icon = severityIcons[alert.severity] || Info;
                                    return (
                                        <div
                                            key={alert.id}
                                            onClick={() => {
                                                if (alert.actionUrl) {
                                                    navigate(alert.actionUrl);
                                                }
                                                handleMarkRead(alert.id, alert.read)
                                            }}
                                            className={`group relative flex gap-4 p-5 transition-all hover:bg-secondary/30 cursor-pointer ${!alert.read ? 'bg-primary/[0.02]' : ''}`}
                                        >
                                            <div className={`mt-1 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${!alert.read ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>

                                            <div className="flex-1 space-y-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <h4 className={`text-base ${!alert.read ? 'font-bold' : 'font-semibold'} text-foreground`}>
                                                        {alert.title}
                                                    </h4>
                                                    <span className="text-xs text-muted-foreground font-medium">
                                                        {timeAgo(alert.createdAt.toString())}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                                                    {alert.message}
                                                </p>
                                            </div>

                                            {!alert.read && (
                                                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                                                    <span className="w-2.5 h-2.5 rounded-full bg-primary shadow-[0_0_10px_rgba(var(--primary),0.5)]" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-8 pt-8 border-t">
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
                </SectionCard>
            </div>
        </Layout>
    );
}
